/**
 * Common Animation Configurations using Framer Motion
 * 
 * Usage:
 * import { fadeIn, fadeInUp, staggerContainer } from '@/utils/animations';
 * 
 * <motion.div variants={fadeIn} initial="hidden" animate="visible">
 *   Content
 * </motion.div>
 * 
 * Or use the custom hook:
 * <motion.div {...fadeInProps}>Content</motion.div>
 */

import type { Variants } from 'framer-motion';  

// ========== FADE ANIMATIONS ==========

export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.6, ease: 'easeOut' }
  }
};

export const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' }
  }
};

export const fadeInDown: Variants = {
  hidden: { opacity: 0, y: -20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' }
  }
};

export const fadeInLeft: Variants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: 'easeOut' }
  }
};

export const fadeInRight: Variants = {
  hidden: { opacity: 0, x: 20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: 'easeOut' }
  }
};

// ========== SCALE ANIMATIONS ==========

export const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, ease: 'easeOut' }
  }
};

export const scaleInBounce: Variants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { 
      duration: 0.6, 
      ease: [0.34, 1.56, 0.64, 1] // Bounce effect
    }
  }
};

// ========== SLIDE ANIMATIONS ==========

export const slideInFromTop: Variants = {
  hidden: { y: -100, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.5, ease: 'easeOut' }
  }
};

export const slideInFromBottom: Variants = {
  hidden: { y: 100, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.5, ease: 'easeOut' }
  }
};

// ========== STAGGER ANIMATIONS (For parent containers) ==========

export const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};

export const staggerContainerFast: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.05
    }
  }
};

export const staggerContainerSlow: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.2
    }
  }
};

// ========== SPEED VARIANTS ==========

export const createFadeInWithDelay = (delay: number): Variants => ({
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.6, delay, ease: 'easeOut' }
  }
});

export const createFadeInUpWithDelay = (delay: number): Variants => ({
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay, ease: 'easeOut' }
  }
});

// ========== CUSTOM TRANSITIONS ==========

export const springTransition = {
  type: 'spring',
  stiffness: 300,
  damping: 30
};

export const smoothTransition = {
  duration: 0.6,
  ease: [0.43, 0.13, 0.23, 0.96]
};

// ========== HOVER & TAP ANIMATIONS ==========

export const buttonHover = {
  scale: 1.05,
  transition: { duration: 0.2 }
};

export const buttonTap = {
  scale: 0.95
};

export const cardHover = {
  y: -5,
  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  transition: { duration: 0.3 }
};

// ========== HELPER FUNCTIONS ==========

/**
 * Quick props for common animations
 * Usage: <motion.div {...fadeInProps}>Content</motion.div>
 */
export const fadeInProps = {
  variants: fadeIn,
  initial: 'hidden',
  animate: 'visible'
};

export const fadeInUpProps = {
  variants: fadeInUp,
  initial: 'hidden',
  animate: 'visible'
};

export const fadeInDownProps = {
  variants: fadeInDown,
  initial: 'hidden',
  animate: 'visible'
};

export const scaleInProps = {
  variants: scaleIn,
  initial: 'hidden',
  animate: 'visible'
};

/**
 * Creates animation props with custom delay
 */
export const createAnimationProps = (
  variant: Variants,
  delay: number = 0
) => ({
  variants: variant,
  initial: 'hidden',
  animate: 'visible',
  transition: { delay }
});

/**
 * Viewport animation - triggers when element enters viewport
 */
export const viewportAnimation = {
  initial: 'hidden',
  whileInView: 'visible',
  viewport: { once: true, amount: 0.3 }
};
